import useUrlPagination from 'hooks/use-url-pagination'
import LegacyOperationsApiV2 from 'api/v2/admins/legacy-operations-api-v2'
import LookupField from 'domains/dashboard-migration/lookup-field'
import { ReadableRecord } from 'domains/dashboard-migration/readable-detail'
import { errorMessage } from 'domains/dashboard-migration/api'
import 'domains/dashboard-migration/legacy.css'
import TitlePage from 'components/atoms/title-page'
import { ALERT_TYPES } from 'constants/alert-constants'
import { LEGACY_OPERATION_RESOURCES } from 'domains/legacy-operation/constants/legacy-operation-resources'
import PropTypes from 'prop-types'
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useHistory, useLocation } from 'react-router-dom'
import {
  Badge,
  Button,
  Card,
  CardBody,
  Col,
  Form,
  FormGroup,
  Input,
  Label,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Row,
  Spinner,
  Table
} from 'reactstrap'

const propTypes = {
  resourceKey : PropTypes.string.isRequired,
  pageUtils   : PropTypes.shape({
    setAlertMsg    : PropTypes.func,
    setApiErrorMsg : PropTypes.func
  })
}

const defaultProps = {
  pageUtils: {
    setAlertMsg    : () => {},
    setApiErrorMsg : () => {}
  }
}

const stateColors = {
  requested          : 'warning',
  received           : 'warning',
  waiting_for_pickup : 'warning',
  confirmed          : 'success',
  rejected           : 'danger',
  cancelled          : 'secondary'
}

const datetimeLocalValue = (value) => {
  if (!value) return ''
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return ''
  const localDate = new Date(date.getTime() - (date.getTimezoneOffset() * 60000))
  return localDate.toISOString().slice(0, 16)
}

const initialValues = (config, record = {}) => (
  (config.fields || []).reduce((memo, field) => {
    if (field.type === 'multi-select') memo[field.key] = record[field.key] || []
    else if (field.type === 'datetime-local') memo[field.key] = datetimeLocalValue(record[field.key])
    else memo[field.key] = record[field.key] ?? ''
    return memo
  }, {})
)

const formatCell = (column, value) => {
  if (column.type === 'datetime') return value ? new Date(value).toLocaleString() : '-'
  if (column.type === 'currency') {
    const amount = Number(value || 0)
    return new Intl.NumberFormat('id-ID', { style: 'currency', currency: 'IDR', maximumFractionDigits: 0 }).format(amount)
  }
  if (column.type === 'boolean') return value ? 'Yes' : 'No'
  if (column.type === 'state') {
    const label = String(value || '-').replaceAll('_', ' ')
    return <Badge color={ stateColors[value] || 'secondary' }>{ label }</Badge>
  }
  return value === null || value === undefined || value === '' ? '-' : String(value)
}

const downloadBlob = (response, fallbackName) => {
  const disposition = response.headers?.['content-disposition'] || ''
  const matchedName = disposition.match(/filename="?([^";]+)"?/i)?.[1]
  const url = window.URL.createObjectURL(new Blob([ response.data ], { type: 'application/pdf' }))
  const link = document.createElement('a')
  link.href = url
  link.setAttribute('download', matchedName || fallbackName)
  document.body.appendChild(link)
  link.click()
  link.remove()
  window.URL.revokeObjectURL(url)
}

const LegacyOperationPage = ({ resourceKey, pageUtils, mainTitle = 'Legacy Features' }) => {
  const previousResource = useRef(resourceKey)
  const config = LEGACY_OPERATION_RESOURCES[resourceKey]
  const history = useHistory()
  const location = useLocation()
  const pickupId = useMemo(() => new URLSearchParams(location.search).get('pickup_id'), [ location.search ])

  const [ records, setRecords ] = useState([])
  const { page, setPage } = useUrlPagination()
  const [ meta, setMeta ] = useState({ total: 0, per_page: 25 })
  const [ archived, setArchived ] = useState(false)
  const [ error, setError ] = useState('')
  const [ modalMode, setModalMode ] = useState('edit')
  const [ detailLoading, setDetailLoading ] = useState(false)
  const fetchSequence = useRef(0)
  const detailSequence = useRef(0)
  const archiveOperation = useRef(false)
  const showError = useCallback(async (err) => setError(await errorMessage(err)), [])
  const [ options, setOptions ] = useState({})
  const [ selectedRecord, setSelectedRecord ] = useState(null)
  const [ values, setValues ] = useState(() => initialValues(config))
  const [ queryInput, setQueryInput ] = useState('')
  const [ query, setQuery ] = useState('')
  const [ state, setState ] = useState(config.defaultState || '')
  const [ specificDate, setSpecificDate ] = useState('')
  const [ isLoading, setIsLoading ] = useState(false)
  const [ isSaving, setIsSaving ] = useState(false)
  const [ isModalOpen, setIsModalOpen ] = useState(false)

  useEffect(() => {
    if (previousResource.current !== resourceKey) setPage(1)
    previousResource.current = resourceKey
    setArchived(false)
    setError('')
    setRecords([])
    setOptions({})
    setSelectedRecord(null)
    setValues(initialValues(config))
    setQueryInput('')
    setQuery('')
    setState(config.defaultState || '')
    setSpecificDate('')
    setIsModalOpen(false)
  }, [ config, resourceKey ])

  const fetchRecords = useCallback(() => {
    const sequence = ++fetchSequence.current
    setError('')
    setIsLoading(true)
    const params = {
      page,
      archived: String(archived),
      query: query || undefined,
      state: config.stateFilter ? state : undefined,
      specific_date: config.specificDate ? specificDate : undefined,
      pickup_id: resourceKey === 'pickup_products' || resourceKey === 'pickups' ? pickupId || undefined : undefined
    }

    LegacyOperationsApiV2.index(resourceKey, params)
      .then((response) => { if (sequence === fetchSequence.current) { setRecords(response.data[resourceKey] || []); setMeta(response.data.meta || { total: 0, per_page: 25 }) } })
      .catch(showError)
      .finally(() => { if (sequence === fetchSequence.current) setIsLoading(false) })
  }, [ config.specificDate, config.stateFilter, pickupId, query, resourceKey, specificDate, state, page, archived, showError ])

  useEffect(() => {
    fetchRecords()
  }, [ fetchRecords ])

  const fetchOptions = useCallback(() => {
    if (!config.options || resourceKey === 'carts') return Promise.resolve({})
    return LegacyOperationsApiV2.options(resourceKey)
      .then((response) => {
        setOptions(response.data || {})
        return response.data || {}
      })
      .catch((error) => {
        pageUtils.setApiErrorMsg?.(error.response?.data || error.message)
        throw error
      })
  }, [ config.options, pageUtils, resourceKey ])

  const closeModal = () => {
    if (isSaving) return
    detailSequence.current += 1
    setIsModalOpen(false)
    setSelectedRecord(null)
    setValues(initialValues(config))
  }

  const openCreate = () => {
    const open = () => {
      setModalMode('edit')
      setError('')
      setSelectedRecord(null)
      setValues(initialValues(config))
      setIsModalOpen(true)
    }
    config.options ? fetchOptions().then(open).catch(() => {}) : open()
  }

  const openEdit = (record) => {
    const sequence = ++detailSequence.current
    setModalMode('edit')
    setError('')
    setDetailLoading(true)
    setSelectedRecord(record)
    setValues(initialValues(config, record))
    setIsModalOpen(true)
    Promise.all([
      LegacyOperationsApiV2.show(resourceKey, record.id, archived),
      config.options ? fetchOptions() : Promise.resolve()
    ])
      .then(([ response ]) => {
        if (sequence !== detailSequence.current) return
        const responseKey = resourceKey.replace(/s$/, '')
        const detail = response.data[responseKey] || record
        setSelectedRecord(detail)
        setValues(initialValues(config, detail))
        setIsModalOpen(true)
      })
      .catch(showError)
      .finally(() => { if (sequence === detailSequence.current) setDetailLoading(false) })
  }

  const openDetail = async (record) => {
    const sequence = ++detailSequence.current
    setModalMode('view'); setError(''); setSelectedRecord(record); setIsModalOpen(true); setDetailLoading(true)
    try {
      const response = await LegacyOperationsApiV2.show(resourceKey, record.id, archived)
      if (sequence === detailSequence.current) setSelectedRecord(response.data[resourceKey.replace(/s$/, '')] || record)
    } catch (err) { showError(err) }
    finally { if (sequence === detailSequence.current) setDetailLoading(false) }
  }

  const changeArchive = async (record) => {
    if (archiveOperation.current) return
    const restoring = Boolean(record.legacy_archived_at)
    if (!window.confirm(`${restoring ? 'Restore' : 'Archive'} ${config.singular} #${record.id}? Data dan relasi tetap disimpan.`)) return
    archiveOperation.current = true
    setIsSaving(true)
    try {
      if (restoring) await LegacyOperationsApiV2.restore(resourceKey, record.id)
      else await LegacyOperationsApiV2.archive(resourceKey, record.id)
      pageUtils.setAlertMsg?.(`${config.singular} ${restoring ? 'restored' : 'archived'}.`, ALERT_TYPES.SUCCESS)
      fetchRecords()
    } catch (err) { showError(err) }
    finally { archiveOperation.current = false; setIsSaving(false) }
  }

  const handleChange = (field, event) => {
    const nextValue = field.type === 'multi-select'
      ? Array.from(event.target.selectedOptions).map((option) => option.value)
      : event.target.value
    setValues((current) => ({ ...current, [field.key]: nextValue }))
  }

  const downloadPdf = (id) => {
    LegacyOperationsApiV2.downloadTandaKeluarPdf(id)
      .then((response) => downloadBlob(response, `tanda-keluar-${id}.pdf`))
      .catch(showError)
  }

  const handleSubmit = (event) => {
    event.preventDefault()
    setIsSaving(true)

    const request = selectedRecord
      ? LegacyOperationsApiV2.update(resourceKey, selectedRecord.id, values)
      : LegacyOperationsApiV2.create(resourceKey, values)

    request
      .then((response) => {
        pageUtils.setAlertMsg?.(`${config.singular} successfully saved.`, ALERT_TYPES.SUCCESS)
        closeModal()
        fetchRecords()
        if (resourceKey === 'tanda_keluars' && !selectedRecord) {
          const created = response.data.tanda_keluar
          if (created?.id) downloadPdf(created.id)
        }
      })
      .catch(showError)
      .finally(() => setIsSaving(false))
  }

  const handleTransition = (record, action) => {
    let rejectionNote = ''
    if (action === 'reject' && [ 'pickups', 'pickup_products' ].includes(resourceKey)) {
      const answer = window.prompt('Rejection Note')
      if (answer === null) return
      rejectionNote = answer
    }

    if (!window.confirm(`${action === 'confirm' ? 'Confirm' : 'Reject'} ${config.singular}?`)) return

    const request = action === 'confirm'
      ? LegacyOperationsApiV2.confirm(resourceKey, record.id)
      : LegacyOperationsApiV2.reject(resourceKey, record.id, rejectionNote)

    request
      .then(() => {
        pageUtils.setAlertMsg?.(`${config.singular} successfully ${action === 'confirm' ? 'confirmed' : 'rejected'}.`, ALERT_TYPES.SUCCESS)
        fetchRecords()
      })
      .catch(showError)
  }

  const renderFieldOptions = (field) => {
    if (field.optionGroups) {
      return field.optionGroups.map((group) => (
        <optgroup key={ group.key } label={ group.label }>
          { (options[group.key] || []).map((option) => (
            <option key={ `${group.key}-${option.id}` } value={ option.id }>{ option.label }</option>
          )) }
        </optgroup>
      ))
    }

    return (options[field.optionsKey] || []).map((option) => (
      <option key={ option.id } value={ option.id }>{ option.label }</option>
    ))
  }

  const renderActions = (record) => (
    <div className='d-flex flex-wrap' style={ { gap: '6px' } }>
      <Button color='info' size='sm' disabled={ detailLoading } onClick={ () => openDetail(record) }>Detail</Button>
      <Button color={ record.legacy_archived_at ? 'success' : 'warning' } size='sm' disabled={ isSaving } onClick={ () => changeArchive(record) }>{ record.legacy_archived_at ? 'Restore' : 'Archive' }</Button>
      { !record.legacy_archived_at && config.edit && (resourceKey !== 'carts' || record.can_edit) && <Button color='primary' size='sm' onClick={ () => openEdit(record) }>{ 'Edit' }</Button> }
      {
        !record.legacy_archived_at && resourceKey === 'transits' && record.state === 'received' && !record.consignment_item && (
          <>
            <Button color='success' size='sm' onClick={ () => handleTransition(record, 'confirm') }>{ 'Confirm' }</Button>
            <Button color='danger' size='sm' onClick={ () => handleTransition(record, 'reject') }>{ 'Reject' }</Button>
          </>
        )
      }
      {
        resourceKey === 'tanda_keluars' && (
          <Button color='success' size='sm' onClick={ () => downloadPdf(record.id) }>{ 'Download PDF' }</Button>
        )
      }
      {
        resourceKey === 'pickups' && (
          <Button
            color='info'
            size='sm'
            onClick={ () => history.push(`/app/legacy-operations/pickup-products?pickup_id=${record.id}`) }
          >
            { 'Products' }
          </Button>
        )
      }
      {
        !record.legacy_archived_at && resourceKey === 'pickups' && record.state === 'requested' && (
          <>
            <Button color='success' size='sm' onClick={ () => handleTransition(record, 'confirm') }>{ 'Confirm' }</Button>
            <Button color='danger' size='sm' onClick={ () => handleTransition(record, 'reject') }>{ 'Reject' }</Button>
          </>
        )
      }
      {
        !record.legacy_archived_at && resourceKey === 'pickup_products' && record.state === 'waiting_for_pickup' && record.pickup_state === 'confirmed' && (
          <>
            <Button color='success' size='sm' onClick={ () => handleTransition(record, 'confirm') }>{ 'Confirm' }</Button>
            <Button color='danger' size='sm' onClick={ () => handleTransition(record, 'reject') }>{ 'Reject' }</Button>
          </>
        )
      }
      {
        !record.legacy_archived_at && resourceKey === 'pickup_products' && record.state === 'waiting_for_pickup' && record.pickup_state !== 'confirmed' && (
          <Button
            color='secondary'
            size='sm'
            onClick={ () => history.push(`/app/legacy-operations/pickups?pickup_id=${record.pickup_id}`) }
          >
            { 'Confirm Pickup First' }
          </Button>
        )
      }
    </div>
  )

  const visibleFields = (config.fields || []).filter((field) => !(field.createOnly && selectedRecord))

  return (
    <>
      <TitlePage mainTitle={ mainTitle } subTitle={ config.title } />
      { resourceKey === 'carts' && <p className='text-muted'>Edit mempertahankan harga dan jumlah item yang sudah ada; produk baru memakai selling price saat ini. Cart yang sudah terkait Deal hanya dapat dibaca.</p> }
      { resourceKey === 'pickup_products' && <p className='text-muted'>Setiap baris adalah satu produk dari Pickup. SKU, status penerimaan, dan penolakan dicatat per produk.</p> }
      { resourceKey === 'pickups' && <p className='text-muted'>Setiap baris adalah satu permintaan Pickup. Klik Products untuk melihat daftar produk di dalamnya.</p> }
      { error && !isModalOpen && <div role='alert' className='alert alert-danger'>{ error }</div> }
      <Card>
        <CardBody>
          <Form
            className='mb-3'
            onSubmit={ (event) => {
              event.preventDefault()
              setPage(1)
              setQuery(queryInput.trim())
            } }
          >
            <Row className='legacy-operation-toolbar align-items-end mb-3'>
              <Col md='4'>
                <FormGroup>
                  <Label>{ 'Search' }</Label>
                  <Input
                    value={ queryInput }
                    placeholder={ `Search ${config.title}` }
                    onChange={ (event) => setQueryInput(event.target.value) }
                  />
                </FormGroup>
              </Col>
              {
                config.stateFilter && (
                  <Col md='3'>
                    <FormGroup>
                      <Label>{ 'State' }</Label>
                      <Input type='select' value={ state } onChange={ (event) => { setPage(1); setState(event.target.value) } }>
                        <option value=''>{ 'All' }</option>
                        { config.states.map((item) => <option key={ item } value={ item }>{ item.replaceAll('_', ' ') }</option>) }
                      </Input>
                    </FormGroup>
                  </Col>
                )
              }
              {
                config.specificDate && (
                  <Col md='3'>
                    <FormGroup>
                      <Label>{ 'Specific Date' }</Label>
                      <Input type='date' value={ specificDate } onChange={ (event) => { setPage(1); setSpecificDate(event.target.value) } } />
                    </FormGroup>
                  </Col>
                )
              }
              <Col md={ config.stateFilter || config.specificDate ? 5 : 8 } className='legacy-toolbar-actions'>
                <Button color='secondary' type='submit'>{ 'Search' }</Button>
                { !archived && config.create && <Button type='button' color='primary' onClick={ openCreate }>{ `Create ${config.singular}` }</Button> }
              </Col>
            </Row>
          </Form>

          <div className='d-flex justify-content-between align-items-center mb-3'>
            <div className='text-muted'>{ pickupId ? `Pickup ID: ${pickupId}` : '' }</div>
            <FormGroup style={ { maxWidth: 260 } }><Label>Status data</Label><Input type='select' aria-label='Status data arsip' value={ String(archived) } onChange={ (event) => { setArchived(event.target.value === 'true'); setPage(1); setState('') } }><option value='false'>Data utama</option><option value='true'>Arsip</option></Input></FormGroup>
          </div>

          <div className='table-responsive'>
            <Table bordered hover size='sm'>
              <thead>
                <tr>
                  { config.columns.map((column) => <th key={ column.key }>{ column.label }</th>) }
                  <th>{ 'Actions' }</th>
                </tr>
              </thead>
              <tbody>
                {
                  isLoading
                    ? (
                      <tr><td colSpan={ config.columns.length + 1 } className='text-center py-4'><Spinner size='sm' /></td></tr>
                    )
                    : records.length === 0
                      ? <tr><td colSpan={ config.columns.length + 1 } className='text-center py-4'>{ 'No data found.' }</td></tr>
                      : records.map((record) => (
                        <tr key={ record.id }>
                          { config.columns.map((column) => <td key={ column.key }>{ formatCell(column, record[column.key]) }</td>) }
                          <td>{ renderActions(record) }</td>
                        </tr>
                      ))
                }
              </tbody>
            </Table>
          </div>
          <div className='d-flex align-items-center mt-3'><Button disabled={ page <= 1 || isLoading } onClick={ () => setPage(page - 1) }>Previous</Button><span className='mx-3'>Halaman { page } / { Math.max(1, Math.ceil(meta.total / meta.per_page)) } · { meta.total } data</span><Button disabled={ isLoading || page * meta.per_page >= meta.total } onClick={ () => setPage(page + 1) }>Next</Button></div>
        </CardBody>
      </Card>

      <Modal isOpen={ isModalOpen } toggle={ closeModal } size='xl' className='legacy-resource-editor'>
        <Form onSubmit={ handleSubmit }>
          <ModalHeader toggle={ closeModal }>{ `${modalMode === 'view' ? 'Detail' : selectedRecord ? 'Edit' : 'Create'} ${config.singular}` }</ModalHeader>
          <ModalBody>
            { error && <div role='alert' className='alert alert-danger'>{ error }</div> }
            { detailLoading && <p role='status'>Memuat detail...</p> }
            { modalMode === 'view' ? <ReadableRecord record={ selectedRecord } omit={ ['can_edit'] } /> : !detailLoading && visibleFields.map((field) => (
              <FormGroup key={ field.key }>
                <Label>{ field.label }</Label>
                {
                  resourceKey === 'carts' && field.key === 'product_ids' ? <LookupField base={ `/api/v2/admins/legacy_operations/${resourceKey}` } field={ { label: 'Products', kind: 'products', multiple: true } } value={ values.product_ids } initialOptions={ (selectedRecord?.items || []).map((item) => ({ value: item.product_id, label: `${item.sku || item.product_id} - ${item.name || ''}` })) } onChange={ (value) => setValues((current) => ({ ...current, product_ids: value })) } /> : [ 'select', 'multi-select' ].includes(field.type)
                    ? (
                      <Input
                        type='select'
                        multiple={ field.type === 'multi-select' }
                        value={ values[field.key] }
                        required={ field.required }
                        onChange={ (event) => handleChange(field, event) }
                      >
                        { field.type === 'select' && field.allowBlank && <option value=''>{ 'No User Account' }</option> }
                        { field.type === 'select' && !field.allowBlank && <option value=''>{ `Select ${field.label}` }</option> }
                        { renderFieldOptions(field) }
                      </Input>
                    )
                    : (
                      <Input
                        type={ field.type }
                        value={ values[field.key] }
                        required={ field.required }
                        onChange={ (event) => handleChange(field, event) }
                      />
                    )
                }
              </FormGroup>
            )) }
          </ModalBody>
          <ModalFooter>
            <Button color='secondary' type='button' onClick={ closeModal } disabled={ isSaving }>{ 'Cancel' }</Button>
            { modalMode !== 'view' && <Button color='primary' type='submit' disabled={ isSaving || detailLoading || Boolean(error) }>{ isSaving ? 'Saving...' : 'Save' }</Button> }
          </ModalFooter>
        </Form>
      </Modal>
    </>
  )
}

LegacyOperationPage.propTypes = propTypes
LegacyOperationPage.defaultProps = defaultProps

export default LegacyOperationPage
